/**
 * When "Później" puts an item back — the four terms, on the trainer's own wall clock.
 *
 * R6 says a snooze names a time and that the item returns once the time has passed if its condition
 * still holds. It does not say WHICH time, so the choice is the client's. The phone offers four
 * (`features/clients/model/snooze.ts`, KANON `bmSnooze`) and this file is the same four, with the
 * same rules, so a trainer who defers "za 3 dni" on one surface means the same thing on the other.
 *
 * The Pulpit writes one of them — `tomorrowMorning`, the default — and its behaviour is unchanged.
 * `/zadania` offers the menu.
 *
 * ── The browser's zone, and no zone field on the wire ────────────────────────────────────────
 * `RecordAttentionResponseRequest.snoozeUntil` is a single instant with nowhere to put a zone, so
 * the wall clock has to be resolved before the write. `Date`'s own setters are local-time setters,
 * which means the browser's zone is applied by the platform rather than computed here — and that
 * includes the two cases hand arithmetic gets wrong: the day a clock jumps forward (08:00 still
 * exists and is simply an hour closer) and the day it falls back (08:00 resolves to the second
 * occurrence, an hour later in UTC, which is still 08:00 on the trainer's wall). Adding
 * 86_400_000ms across that night lands at 07:00 and breaks the promise the label made.
 *
 * `now` is a parameter and never `new Date()`, which is what makes the month end, the year end and
 * both DST edges reachable from a test.
 *
 * ── "Dziś wieczorem" disappears rather than resolving into the past ──────────────────────────
 * Past 17:00 it would name an instant that has been and gone, which R6 refuses and which reads as a
 * broken row. The phone hides it at the same hour; a permanently disabled row was rejected there
 * for the same reason it would be here.
 */

/** The morning three of the four terms come back on. */
const MORNING_HOUR = 8;
/** The evening the first term names. */
const EVENING_HOUR = 18;
/** Past this hour "today evening" is no longer offered — its target is behind the trainer. */
const EVENING_CUTOFF_HOUR = 17;
/** Which of the four a surface writes when it offers no choice. */
const DEFAULT_PRESET: SnoozePresetId = 'tomorrowMorning';

/** `Date#getDay`'s Monday. */
const MONDAY = 1;
/** How far "za 3 dni" reaches. */
const THREE_DAYS = 3;
const DAYS_IN_WEEK = 7;

/** Which term. The id is also the tail of its i18n key under `clients.tasks.snooze.presets`. */
export type SnoozePresetId = 'todayEvening' | 'tomorrowMorning' | 'inThreeDays' | 'nextWeek';

export interface SnoozePreset {
  readonly id: SnoozePresetId;
  /** A key in mobile's verbatim bundle — the two apps name the four terms identically. */
  readonly labelKey: string;
  /** The instant the wire carries, ISO 8601 in UTC. Always strictly after `now`. */
  readonly untilIso: string;
  /** The same instant as a `Date`, for a surface that words it in the reader's own locale. */
  readonly until: Date;
  /** The term a surface takes when it offers no choice. Exactly one of the four. */
  readonly isDefault: boolean;
}

function atHour(base: Date, hour: number): Date {
  const at = new Date(base.getTime());
  at.setHours(hour, 0, 0, 0);
  return at;
}

function plusDays(base: Date, days: number): Date {
  const at = new Date(base.getTime());
  // `setDate` past the end of a month rolls the month, and past December rolls the year — the
  // platform's own arithmetic, which is why no branch here handles either.
  at.setDate(at.getDate() + days);
  return at;
}

/** The next Monday STRICTLY after `now`, at the morning hour. Monday itself means the one after. */
function nextMonday(now: Date): Date {
  const ahead = (MONDAY - now.getDay() + DAYS_IN_WEEK) % DAYS_IN_WEEK;
  return atHour(plusDays(now, ahead === 0 ? DAYS_IN_WEEK : ahead), MORNING_HOUR);
}

function preset(id: SnoozePresetId, until: Date, isDefault: boolean): SnoozePreset {
  return {
    id,
    labelKey: `clients.tasks.snooze.presets.${id}`,
    untilIso: until.toISOString(),
    until,
    isDefault,
  };
}

/**
 * The terms on offer at `now`, in the order the phone lists them.
 *
 * Three or four, never fewer: only the evening one can drop out.
 */
export function snoozePresets(now: Date): readonly SnoozePreset[] {
  const terms: SnoozePreset[] = [];
  if (now.getHours() < EVENING_CUTOFF_HOUR) {
    terms.push(preset('todayEvening', atHour(now, EVENING_HOUR), false));
  }
  terms.push(preset('tomorrowMorning', atHour(plusDays(now, 1), MORNING_HOUR), true));
  terms.push(preset('inThreeDays', atHour(plusDays(now, THREE_DAYS), MORNING_HOUR), false));
  terms.push(preset('nextWeek', nextMonday(now), false));
  return terms;
}

/**
 * A term the trainer chose, resolved again at the moment of the DECISION.
 *
 * The menu is drawn from the clock of when the screen was opened, and a tab is left open: opened on
 * Monday morning and used on Tuesday, "Jutro rano" would still carry Tuesday 08:00, which has been
 * and gone — and R6 refuses an instant that is not in the future, so every snooze would bounce. The
 * chosen TERM is what the trainer meant; its instant belongs to the press. A term no longer on
 * offer at that moment (the evening one, past its cutoff) falls back to the default.
 */
export function snoozePresetAt(id: SnoozePresetId, now: Date): SnoozePreset {
  const offered = snoozePresets(now);
  const chosen = offered.find((term) => term.id === id);
  if (chosen !== undefined) return chosen;
  const fallback = offered.find((term) => term.isDefault);
  if (fallback === undefined) throw new Error('snoozePresets always offers a default term');
  return fallback;
}

/**
 * Tomorrow at 08:00 local, as the UTC instant the wire carries.
 *
 * The default term, and what the Pulpit writes without asking. Always tomorrow, never "today at
 * 08:00 if it has not happened yet": a task deferred at 07:00 would otherwise return an hour later,
 * which is not what the trainer asked for. R6 requires a future instant and this is unconditionally
 * one.
 */
export function snoozeUntil(now: Date): string {
  // Through the presets rather than repeating their arithmetic: "the default term" is one fact, and
  // a second copy of the formula here could drift from the term the menu offers under the same name.
  return snoozePresetAt(DEFAULT_PRESET, now).untilIso;
}

/** How a term's instant is worded. Both come from the surface, because `model/` has no locale. */
export interface SnoozeTermFormats {
  /** The hour alone: "18:00". */
  readonly clock: (until: Date) => string;
  /** The day and the hour: "pon., 08:00". */
  readonly dayAndClock: (until: Date) => string;
}

/**
 * **What a term is called once it has resolved** — "18:00", "śr., 08:00", "pon., 08:00".
 *
 * One wording, two readers: the menu prints it beside the term's own word, and the live region says
 * it back after the write. They were separate before, and the live region said "jutro" whichever
 * term the trainer had picked — R6 is about a CHOSEN time, and a screen that writes next Monday
 * while announcing tomorrow is telling a reader who cannot see the menu the wrong thing.
 *
 * A term landing later TODAY names only the hour, because the day is the one the reader is already
 * in and "dziś, 18:00" says it twice. Every other term names both.
 */
export function snoozeTermWords(
  preset: SnoozePreset,
  now: Date,
  words: SnoozeTermFormats,
): string {
  const sameDay =
    preset.until.getFullYear() === now.getFullYear() &&
    preset.until.getMonth() === now.getMonth() &&
    preset.until.getDate() === now.getDate();
  return sameDay ? words.clock(preset.until) : words.dayAndClock(preset.until);
}
